import { useEffect, useState } from "react";
import type { SceneLook } from "./sequencer";
import { usePhysics } from "./hooks/usePhysics";
import { useShowLoader } from "./hooks/useShowLoader";
import { useSwarmSource } from "./hooks/useSwarmSource";
import { useSequencer } from "./hooks/useSequencer";
import { useFpv } from "./hooks/useFpv";
import { useRadio } from "./hooks/useRadio";
import { useAudio } from "./hooks/useAudio";
import { useFlightTape } from "./hooks/useFlightTape";
import { usePersistent } from "./hooks/usePersistent";
import { useEdgeReveal } from "./hooks/useEdgeReveal";
import { StudioScene } from "./components/StudioScene";
import { ShowTransport } from "./components/ShowTransport";
import { SourceControls } from "./components/SourceControls";
import { Timeline } from "./components/Timeline";
import { FpvHud } from "./components/FpvHud";
import { FpvPanel } from "./components/FpvPanel";
import { ReplayHud } from "./components/ReplayHud";
import "./studio.css";

type Mode = "studio" | "sequencer";
type PanelMode = "open" | "auto" | "closed";

/**
 * The light-show simulator. Composes three hooks (source / show / physics) and
 * three views (scene / source-controls / show-transport); each of those owns
 * its own concern so this stays a thin shell.
 */
export default function SwarmStudio() {
  const [error, setError] = useState("");
  const [bloom, setBloom] = useState(true);
  const [bloomStrength, setBloomStrength] = useState(1.15);
  const [brightness, setBrightness] = useState(1.5);
  const [rotate, setRotate] = useState(true);
  const [rotateSpeed, setRotateSpeed] = useState(2); // °/s
  const [constrainView, setConstrainView] = useState(false);
  const [mode, setMode] = useState<Mode>("studio");

  const physics = usePhysics();
  const showLoader = useShowLoader(setError);
  const src = useSwarmSource({ onError: setError });
  const sequencer = useSequencer({ count: src.count, renderMode: src.renderMode, onError: setError });
  const fpv = useFpv();
  const radio = useRadio();
  const audio = useAudio();
  const tape = useFlightTape();
  const seqActive = mode === "sequencer";

  // the sidebar's three lives: open / auto-hide (slide to the left edge) / hidden
  const [panelMode, setPanelMode] = usePersistent<PanelMode>("swarmsim_panel_v1", "open");
  const studioPanel = !fpv.active && mode === "studio";
  const nearLeft = useEdgeReveal("left", panelMode === "auto" && studioPanel);
  const panelVisible = panelMode === "open" || (panelMode === "auto" && nearLeft);

  // FPV flight and tape replay both own the camera — never both at once
  useEffect(() => {
    if (fpv.active && tape.replaying) tape.stopReplay();
    if (!fpv.active) tape.stopRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fpv.active]);
  const startReplay = (id?: string) => {
    fpv.setActive(false);
    tape.startReplay(id);
  };

  // the studio's current colouring, snapshot-ready for "capture" scenes
  const studioLook: SceneLook =
    src.colorMode === "solid" ? { color: "solid", hex: src.solidColor }
    : src.colorMode === "gradient" ? { color: "gradient", stops: src.gradient, axis: src.gradientAxis }
    : { color: "source" };

  return (
    <div className="studio">
      <StudioScene
        cloud={src.cloud}
        sourceScale={src.sourceScale}
        physicsMode={physics.mode}
        physicsParams={physics.params}
        show={showLoader.show}
        playing={showLoader.playing}
        speed={showLoader.speed}
        playhead={showLoader.playhead}
        sliderRef={showLoader.sliderRef}
        brightness={brightness}
        bloom={bloom}
        bloomStrength={bloomStrength}
        autoRotate={rotate && !showLoader.show && !seqActive}
        rotateSpeed={rotateSpeed}
        constrainView={constrainView}
        fpvActive={fpv.active}
        fpvParams={fpv.params}
        fpvTelemetry={fpv.telemetry}
        radio={radio}
        tape={tape}
        sequencerActive={seqActive}
        sequencer={sequencer}
        count={src.count}
        cymDrive={src.cymDrive}
        fx={audio.fx}
        flowDrive={src.flowDrive}
        visStyle={src.visStyle}
      />

      <div className="studio-ui">
        <header className="studio-head">
          {/*
            Absolute, not "/": the studio has its own subdomain now, so a
            root-relative link just reloaded the studio. This is the way home.
          */}
          <a className="studio-back" href="https://www.free.vet" title="Back to free.vet">← free.vet</a>
          <div className="studio-title">
            SWARM STUDIO <span>· {fpv.active ? "FPV flight" : tape.replaying ? "flight replay" : seqActive ? "sequencer" : showLoader.show ? "real show playback" : "live light-show simulator"}</span>
          </div>
          {!fpv.active && (
            <span className="seg mode-seg">
              <button className={"chip seg-btn" + (mode === "studio" ? " active" : "")} onClick={() => setMode("studio")}>Studio</button>
              <button className={"chip seg-btn" + (mode === "sequencer" ? " active" : "")} onClick={() => setMode("sequencer")}>Sequencer</button>
            </span>
          )}
          {!fpv.active && !tape.replaying && tape.tape && (
            <button className="chip" onClick={() => startReplay()} title="Replay the selected flight tape">▶ Flight</button>
          )}
          <button
            className={"chip fpv-toggle" + (fpv.active ? " active" : fpv.connected ? " ready" : "")}
            onClick={() => fpv.setActive((a) => !a)}
            title={fpv.connected ? "Gamepad detected — fly acro FPV" : "Fly FPV (keyboard or gamepad)"}
          >
            {fpv.active ? "Exit FPV" : fpv.connected ? "🎮 FPV" : "🛦 FPV"}
          </button>
        </header>

        {studioPanel && !panelVisible && (
          <button
            className="menu-tab"
            onClick={() => setPanelMode("open")}
            title={panelMode === "auto" ? "Menu auto-hides — slide the pointer to the left edge, or click to pin open" : "Open the menu"}
          >☰ MENU</button>
        )}
        {studioPanel && (
          <div className={"panel" + (panelVisible ? "" : " slid")}>
            <div className="panel-modes">
              <span className="panel-modes-label">MENU</span>
              <span className="seg">
                <button className={"chip seg-btn" + (panelMode === "open" ? " active" : "")} onClick={() => setPanelMode("open")} title="Keep the menu open">Open</button>
                <button className={"chip seg-btn" + (panelMode === "auto" ? " active" : "")} onClick={() => setPanelMode("auto")} title="Auto-hide — the menu returns when the pointer nears the left edge">Auto</button>
                <button className={"chip seg-btn" + (panelMode === "closed" ? " active" : "")} onClick={() => setPanelMode("closed")} title="Hide the menu (a MENU tab stays)">Hide</button>
              </span>
            </div>
            {showLoader.show ? (
              <ShowTransport showLoader={showLoader} bloom={bloom} setBloom={setBloom} brightness={brightness} setBrightness={setBrightness} />
            ) : (
              <SourceControls
                src={src}
                physics={physics}
                showLoader={showLoader}
                bloom={bloom}
                setBloom={setBloom}
                bloomStrength={bloomStrength}
                setBloomStrength={setBloomStrength}
                brightness={brightness}
                setBrightness={setBrightness}
                rotate={rotate}
                setRotate={setRotate}
                rotateSpeed={rotateSpeed}
                setRotateSpeed={setRotateSpeed}
                constrainView={constrainView}
                setConstrainView={setConstrainView}
                audio={audio}
                error={error}
              />
            )}
          </div>
        )}
      </div>

      {!fpv.active && seqActive && (
        <Timeline sq={sequencer} studioSource={src.source} studioLook={studioLook} textInput={src.textInput} />
      )}

      {fpv.active && <FpvHud telemetry={fpv.telemetry} connected={fpv.connected} tape={tape} />}
      {fpv.active && <FpvPanel fpv={fpv} radio={radio} tape={tape} onReplay={startReplay} />}
      {tape.replaying && !fpv.active && (
        <ReplayHud sticks={tape.replaySticks} loop={tape.loop} setLoop={tape.setLoop} onStop={tape.stopReplay} />
      )}
    </div>
  );
}
